import { useEffect, useMemo, useRef, useState } from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsField,
  PortalDsInput,
  PortalDsSelect,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { formatDataTransferApiError } from '../helpers/data-transfers.helpers';
import { snapToThirtyMinuteSlot } from 'src/helpers/scheduler-time.helpers';
import { validateCronExpression } from 'src/helpers/scheduler-schedule.helpers';
import {
  useGetDataTransferSqlMetadataQuery,
  usePreviewDataTransferSqlMutation,
} from 'src/redux/operations/operations.endpoints';
import {
  useGetClientQuery,
  useGetOrganizationClientsQuery,
} from 'src/pages/client-management/endpoints/client.endpoints';
import {
  useGetAccountsQuery,
  useGetCampaignsQuery,
} from 'src/pages/client-management/endpoints/accounts.endpoints';
import type {
  ClientDataTransfer,
  ClientDataTransferWritePayload,
  DataTransferScheduleType,
  SqlPreviewResult,
} from '../types/data-transfers.types';
import { SchedulerTimePicker } from './scheduler-time-picker';
import { ClientSqlReferencePanel } from './client-sql-reference-panel';
import { SqlEditor, type SqlEditorHandle } from './sql-editor';
import { SqlPreviewPanel } from './sql-preview-panel';
import { schedulerDsSegmentButtonClass, schedulerDsSegmentGroup } from './scheduler-ds-button';

const textareaClassName = cn(
  'flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors',
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'
);

export interface ClientDataTransferFormValues {
  name: string;
  description: string;
  ext_client_id: string;
  sql: string;
  schedule_type: DataTransferScheduleType;
  scheduled_time: string;
  cron_expression: string;
  is_enabled: boolean;
}

interface ClientDataTransferFormProps {
  initialTransfer?: ClientDataTransfer | null;
  isSaving?: boolean;
  submitError?: string | null;
  onSubmit: (payload: ClientDataTransferWritePayload) => void;
  onCancel?: () => void;
}

function transferToFormValues(transfer?: ClientDataTransfer | null): ClientDataTransferFormValues {
  return {
    name: transfer?.name ?? '',
    description: transfer?.description ?? '',
    ext_client_id: transfer?.client_ext_id ?? '',
    sql: transfer?.sql ?? '',
    schedule_type: transfer?.schedule_type ?? 'time',
    scheduled_time: snapToThirtyMinuteSlot(transfer?.scheduled_time?.slice(0, 5) ?? undefined),
    cron_expression: transfer?.cron_expression ?? '0 9 * * *',
    is_enabled: transfer?.is_enabled ?? true,
  };
}

export function ClientDataTransferForm({
  initialTransfer,
  isSaving = false,
  submitError = null,
  onSubmit,
  onCancel,
}: ClientDataTransferFormProps) {
  const [values, setValues] = useState<ClientDataTransferFormValues>(() => transferToFormValues(initialTransfer));
  const [previewValidated, setPreviewValidated] = useState(false);
  const [previewAttempted, setPreviewAttempted] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SqlPreviewResult | null>(null);
  const sqlEditorRef = useRef<SqlEditorHandle>(null);

  const { data: clients = [] } = useGetOrganizationClientsQuery();
  const {
    data: selectedClient,
    isFetching: isFetchingClient,
    isError: isClientError,
    error: clientError,
  } = useGetClientQuery(values.ext_client_id, {
    skip: !values.ext_client_id,
  });
  const {
    data: accountsPage,
    isFetching: isFetchingAccounts,
  } = useGetAccountsQuery(undefined, {
    skip: !values.ext_client_id,
  });
  const {
    data: campaignsPage,
    isFetching: isFetchingCampaigns,
  } = useGetCampaignsQuery(undefined, {
    skip: !values.ext_client_id,
  });
  const { data: sqlMetadata } = useGetDataTransferSqlMetadataQuery(
    { extClientId: values.ext_client_id || undefined },
    { skip: !values.ext_client_id }
  );
  const [previewSql, { isLoading: isPreviewing }] = usePreviewDataTransferSqlMutation();

  const referenceAccounts = useMemo(() => {
    const access = selectedClient?.access ?? [];
    const titles = new Map(
      (accountsPage?.items ?? []).map((account) => [account.ext_account_id, account.title])
    );
    const byId = new Map<string, string>();
    for (const row of access) {
      if (!row.account) {
        continue;
      }
      byId.set(row.account, titles.get(row.account) ?? '');
    }
    return [...byId.entries()]
      .map(([account_id, account_name]) => ({ account_id, account_name }))
      .sort((left, right) => (
        left.account_name.localeCompare(right.account_name)
        || left.account_id.localeCompare(right.account_id)
      ));
  }, [accountsPage?.items, selectedClient?.access]);

  const referenceCampaigns = useMemo(() => {
    const access = selectedClient?.access ?? [];
    const titles = new Map(
      (campaignsPage?.items ?? []).map((campaign) => [campaign.ext_campaign_id, campaign.title])
    );
    const byId = new Map<string, string>();
    for (const row of access) {
      if (!row.campaign) {
        continue;
      }
      byId.set(row.campaign, titles.get(row.campaign) ?? '');
    }
    return [...byId.entries()]
      .map(([campaign_id, campaign_name]) => ({ campaign_id, campaign_name }))
      .sort((left, right) => (
        left.campaign_name.localeCompare(right.campaign_name)
        || left.campaign_id.localeCompare(right.campaign_id)
      ));
  }, [campaignsPage?.items, selectedClient?.access]);

  useEffect(() => {
    setValues(transferToFormValues(initialTransfer));
    setPreview(null);
    setPreviewError(null);
    setPreviewValidated(false);
    setPreviewAttempted(false);
  }, [initialTransfer]);

  const updateValues = (patch: Partial<ClientDataTransferFormValues>) => {
    setValues((current) => ({ ...current, ...patch }));
    if (!Object.prototype.hasOwnProperty.call(patch, 'sql')) {
      return;
    }
    setPreviewValidated(false);
    setPreviewAttempted(false);
    setPreview(null);
    setPreviewError(null);
  };

  const cronError = values.schedule_type === 'cron'
    ? validateCronExpression(values.cron_expression)
    : null;

  const saveAsDisabled = previewAttempted && !previewValidated;

  const canSubmit = values.name.trim().length > 0
    && values.ext_client_id
    && values.sql.trim().length > 0
    && (values.schedule_type === 'time' || !cronError)
    && previewAttempted;

  const buildPayload = (): ClientDataTransferWritePayload => ({
    name: values.name.trim(),
    description: values.description.trim(),
    ext_client_id: values.ext_client_id,
    sql: values.sql.trim(),
    schedule_type: values.schedule_type,
    scheduled_time: values.schedule_type === 'time' ? `${values.scheduled_time}:00` : null,
    cron_expression: values.schedule_type === 'cron' ? values.cron_expression.trim() : null,
    is_enabled: previewValidated ? values.is_enabled : false,
  });

  const handlePreview = async() => {
    if (!values.sql.trim()) {
      setPreviewError('Enter SQL before running preview.');
      return;
    }
    setPreviewError(null);
    setPreview(null);
    try {
      const result = await previewSql({ sql: values.sql.trim() }).unwrap();
      const isValid = !result.sample_error;
      setPreview(result);
      setPreviewValidated(isValid);
      setPreviewAttempted(true);
      if (!isValid) {
        setValues((current) => ({ ...current, is_enabled: false }));
      }
    } catch (error) {
      setPreview(null);
      setPreviewError(formatDataTransferApiError(error, 'Preview failed.'));
      setPreviewValidated(false);
      setPreviewAttempted(true);
      setValues((current) => ({ ...current, is_enabled: false }));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!previewAttempted) {
      setPreviewError('Run a preview before saving. If BigQuery rejects the SQL, you can still save it as disabled.');
      return;
    }
    onSubmit(buildPayload());
  };

  return (
    <form
      className="space-y-6"
      onSubmit={handleSubmit}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <PortalDsField
          label="Transfer name"
          htmlFor="transfer-name"
        >
          <PortalDsInput
            id="transfer-name"
            value={values.name}
            required
            onChange={(event) => updateValues({ name: event.target.value })}
          />
        </PortalDsField>
        <PortalDsField
          label="Client"
          htmlFor="transfer-client"
        >
          <PortalDsSelect
            id="transfer-client"
            value={values.ext_client_id}
            disabled={Boolean(initialTransfer)}
            onChange={(event) => updateValues({ ext_client_id: event.target.value })}
          >
            <option value="">
              Select client
            </option>
            {clients.map((client) => (
              <option
                key={client.ext_client_id}
                value={client.ext_client_id}
              >
                {client.title}
              </option>
            ))}
          </PortalDsSelect>
        </PortalDsField>
      </div>

      <PortalDsField
        label="Description"
        htmlFor="transfer-description"
      >
        <textarea
          id="transfer-description"
          rows={3}
          value={values.description}
          className={textareaClassName}
          onChange={(event) => updateValues({ description: event.target.value })}
        />
      </PortalDsField>

      <PortalDsField
        label="SQL"
        htmlFor="transfer-sql"
        hint="Full BigQuery statement to run on schedule (including MERGE / CREATE OR REPLACE / INSERT). Type project.dataset.table for path completions; Control+Space or Option+/ opens suggestions."
      >
        <SqlEditor
          ref={sqlEditorRef}
          id="transfer-sql"
          value={values.sql}
          metadata={sqlMetadata}
          height={420}
          onChange={(sql) => updateValues({ sql })}
        />
      </PortalDsField>

      <ClientSqlReferencePanel
        hasClient={Boolean(values.ext_client_id)}
        accounts={referenceAccounts}
        campaigns={referenceCampaigns}
        isLoading={Boolean(
          values.ext_client_id
          && (isFetchingClient || isFetchingAccounts || isFetchingCampaigns)
          && !selectedClient
        )}
        errorMessage={
          isClientError
            ? formatDataTransferApiError(clientError, 'Could not load client associations.')
            : null
        }
        onInsertId={(id) => {
          sqlEditorRef.current?.insertAtCursor(id);
        }}
      />

      <div className="space-y-3">
        <p className="text-sm font-medium text-black">
          Schedule
        </p>
        <div className={schedulerDsSegmentGroup}>
          <button
            type="button"
            className={schedulerDsSegmentButtonClass(values.schedule_type === 'time')}
            onClick={() => updateValues({ schedule_type: 'time' })}
          >
            Daily time
          </button>
          <button
            type="button"
            className={schedulerDsSegmentButtonClass(values.schedule_type === 'cron')}
            onClick={() => updateValues({ schedule_type: 'cron' })}
          >
            Cron
          </button>
        </div>
        {values.schedule_type === 'time' ? (
          <SchedulerTimePicker
            value={values.scheduled_time}
            onChange={(scheduled_time) => updateValues({ scheduled_time })}
          />
        ) : (
          <PortalDsField
            label="Cron expression"
            htmlFor="transfer-cron"
            error={cronError ?? undefined}
          >
            <PortalDsInput
              id="transfer-cron"
              value={values.cron_expression}
              onChange={(event) => updateValues({ cron_expression: event.target.value })}
            />
          </PortalDsField>
        )}
      </div>

      <label className="flex items-center gap-3 text-sm font-medium text-black">
        <InputSwitch
          checked={values.is_enabled}
          disabled={saveAsDisabled}
          onChange={(event) => updateValues({ is_enabled: event.value })}
        />
        Enabled
      </label>
      {saveAsDisabled ? (
        <PortalDsAlert variant="warning">
          Preview did not succeed, so this transfer will be saved as disabled. Enable it after the SQL dry-run passes.
        </PortalDsAlert>
      ) : null}

      <SqlPreviewPanel
        preview={preview}
        previewError={previewError}
        isPreviewing={isPreviewing}
        previewDisabled={!values.sql.trim()}
        onPreview={handlePreview}
      />

      {submitError ? (
        <PortalDsAlert variant="danger">
          {submitError}
        </PortalDsAlert>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <PortalDsButton
          type="submit"
          disabled={!canSubmit || isSaving}
        >
          {isSaving
            ? 'Saving…'
            : saveAsDisabled
              ? (initialTransfer ? 'Save as disabled' : 'Create as disabled')
              : (initialTransfer ? 'Save transfer' : 'Create transfer')}
        </PortalDsButton>
        {onCancel ? (
          <PortalDsButton
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </PortalDsButton>
        ) : null}
      </div>
    </form>
  );
}
